import { publicView, standings, type Match, type Seat } from '@basgan/rules';
import type { GameEvent, PublicSeat, ViewMessage } from './index.js';

/**
 * İki durum arasındaki farkı olaylara çevirir.
 *
 * Kural motorunun kendi `history` kaydından türetiliyor, elle yeniden inşa
 * edilmiyor — animasyonla gerçek durumun ayrışması yapısal olarak imkânsız.
 *
 * Burada, paylaşılan pakette duruyor çünkü İKİ yer bunu üretiyor: Colyseus
 * odası (online) ve tarayıcıdaki yerel oyun (botlara karşı). Kopyalansaydı
 * ikisi zamanla ayrışır, masa bileşeni moda göre farklı davranırdı.
 */
export function deriveEvents(before: Match, after: Match): GameEvent[] {
  const consumed = before.deal?.history.length ?? 0;
  const sameDeal = after.dealIndex === before.dealIndex;
  // El bittiyse son hamlelerin kaydı `lastDeal`'da kaldı.
  const source = sameDeal ? after.deal : after.lastDeal;

  const events: GameEvent[] = (source?.history.slice(consumed) ?? []).map((entry): GameEvent => {
    switch (entry.kind) {
      case 'play':
        return { kind: 'play', seat: entry.seat, cards: entry.cards };
      case 'pass':
        return { kind: 'pass', seat: entry.seat };
      case 'cut':
        return { kind: 'cut', seat: entry.seat };
      case 'trickEnd':
        return { kind: 'trickEnd', winner: entry.winner, nextLeader: entry.nextLeader };
      case 'finish':
        return { kind: 'finish', seat: entry.seat, place: entry.place };
    }
  });

  if (sameDeal) return events;

  events.push({
    kind: 'dealEnd',
    order: [...after.lastFinishOrder!],
    scores: [...after.scores],
  });

  if (after.phase === 'matchOver') {
    events.push({ kind: 'matchOver', standings: standings(after) });
    return events;
  }

  if (after.exchange && after.exchange.forced.length > 0) {
    events.push({
      kind: 'exchangeStart',
      transfers: after.exchange.forced.map((t) => ({
        from: t.from,
        to: t.to,
        count: t.cards.length,
      })),
    });
  }
  events.push({ kind: 'dealStart', dealIndex: after.dealIndex, leader: after.deal!.turn });
  return events;
}

/** Oyuncuya gidecek mesajı kurar. Online ve yerel modda aynı şekil. */
export function buildViewMessage(
  match: Match,
  seat: Seat,
  extra: {
    seq: number;
    seats: PublicSeat[];
    deadline: number | null;
    deadlineTotal: number | null;
  },
): ViewMessage {
  return {
    ...publicView(match, seat),
    seq: extra.seq,
    you: seat,
    seats: extra.seats,
    deadline: extra.deadline,
    deadlineTotal: extra.deadlineTotal,
  };
}
